import React, { useEffect, useState } from 'react'

import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'

import {
  AvailableFilters,
  CatalogCustomAttributeType
} from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'shared/components/common/button'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import ToggleSwitch from 'src/shared/components/common/forms/ToggleSwitch'
import { t } from 'src/translations'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'

import { PriceRangeSlider } from './PriceRangeSlider'
import {
  BrandFilter,
  CountriesOfOriginFilter,
  CustomAttributeFilter,
  OptionFilter,
  TagsFilter
} from './filters'
import {
  FilterType,
  OrderedFilterGroup,
  filterLabel
} from './filterSettingsOrder'
import { useFilterToggle } from './useFilterToggle'

// Option/CustomAttribute excluded so each instance must use its name-keyed template literal.
type FilterSectionKey =
  | Exclude<FilterType, FilterType.Option | FilterType.CustomAttribute>
  | `option:${string}`
  | `customAttribute:${string}`

const optionSectionKey = (name: string) => `option:${name}` as const
const customAttributeSectionKey = (name: string) =>
  `customAttribute:${name}` as const

type FilterPanelProps = {
  isOpen: boolean
  onClose: () => void
  availableFilters?: AvailableFilters | null
  hideOutOfStockItems?: boolean | null
  // Tag/option groups in restaurant-configured order. Null when the store has no
  // filterSettings, in which case every available tag and option is shown with
  // tags before options (the default).
  orderedFilterGroups?: OrderedFilterGroup[] | null
}

export const FilterPanel = ({
  isOpen,
  onClose,
  availableFilters,
  hideOutOfStockItems,
  orderedFilterGroups
}: FilterPanelProps) => {
  const {
    selectedTags,
    selectedProductOptions,
    selectedCustomAttributes,
    selectedPriceRange,
    selectedBrands,
    selectedCountriesOfOrigin,
    inStockOnly,
    handleTagToggle,
    handleProductOptionToggle,
    handleCustomAttributeToggle,
    handlePriceRangeChange,
    handleInStockOnlyToggle,
    handleBrandToggle,
    handleCountryToggle,
    clearAll,
    apply,
    syncFromContext
  } = useFilterToggle({ staged: true })

  useEffect(() => {
    if (isOpen) {
      syncFromContext!()
    }
  }, [isOpen, syncFromContext])

  // Tag/option groups in the restaurant's configured order. Empty when no
  // filterSettings are enabled, in which case only Price/Availability show.
  const filterGroups = orderedFilterGroups ?? []

  const priceBounds = availableFilters?.price ?? null
  const showAvailabilityFilter = !hideOutOfStockItems

  const [openSection, setOpenSection] = useState<FilterSectionKey | null>(
    FilterType.Price
  )

  // The section whose open animation has finished. Only one section is open at
  // a time, so a single key tracks which one's content is safe to fully mount.
  const [enteredSection, setEnteredSection] = useState<FilterSectionKey | null>(
    null
  )

  const handleSectionToggle = (key: FilterSectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key))
  }

  const sectionProps = (key: FilterSectionKey) => ({
    expanded: openSection === key,
    onToggle: () => handleSectionToggle(key),
    onEntered: () => setEnteredSection(key),
    onExit: () => setEnteredSection((prev) => (prev === key ? null : prev))
  })

  const activeFilterCount =
    selectedTags.length +
    selectedProductOptions.reduce((sum, o) => sum + o.values.length, 0) +
    selectedCustomAttributes.reduce((sum, a) => sum + a.values.length, 0) +
    (selectedPriceRange ? 1 : 0) +
    (inStockOnly ? 1 : 0) +
    selectedBrands.length +
    selectedCountriesOfOrigin.length

  const handleApply = () => {
    apply!()
    onClose()
  }

  const buildFilterSection = (
    group: OrderedFilterGroup
  ): {
    key: FilterSectionKey
    title: string
    inline?: boolean
    content: React.ReactNode
  } => {
    const title = filterLabel(group)
    switch (group.filterType) {
      case FilterType.Tags:
        return {
          key: FilterType.Tags,
          title,
          content: (
            <TagsFilter
              tags={group.tags}
              variant='panel'
              hasEntered={enteredSection === FilterType.Tags}
              selectedTags={selectedTags}
              handleTagToggle={handleTagToggle}
            />
          )
        }
      case FilterType.CustomAttribute: {
        const attribute = group.customAttribute
        const sectionKey = customAttributeSectionKey(attribute.name)
        const isBoolean = attribute.type === CatalogCustomAttributeType.Boolean
        return {
          key: sectionKey,
          title,
          inline: isBoolean,
          content: (
            <CustomAttributeFilter
              attribute={attribute}
              variant='panel'
              hasEntered={!isBoolean && enteredSection === sectionKey}
              selectedCustomAttributes={selectedCustomAttributes}
              handleCustomAttributeToggle={handleCustomAttributeToggle}
            />
          )
        }
      }
      case FilterType.Brand:
        return {
          key: FilterType.Brand,
          title,
          content: (
            <BrandFilter
              brands={group.brands}
              variant='panel'
              hasEntered={enteredSection === FilterType.Brand}
              selectedBrands={selectedBrands}
              handleBrandToggle={handleBrandToggle}
            />
          )
        }
      case FilterType.CountriesOfOrigin:
        return {
          key: FilterType.CountriesOfOrigin,
          title,
          content: (
            <CountriesOfOriginFilter
              countryCodes={group.countryCodes}
              variant='panel'
              hasEntered={enteredSection === FilterType.CountriesOfOrigin}
              selectedCountriesOfOrigin={selectedCountriesOfOrigin}
              handleCountryToggle={handleCountryToggle}
            />
          )
        }
      case FilterType.Option: {
        const sectionKey = optionSectionKey(group.option.name)
        return {
          key: sectionKey,
          title,
          content: (
            <OptionFilter
              option={group.option}
              variant='panel'
              hasEntered={enteredSection === sectionKey}
              selectedProductOptions={selectedProductOptions}
              handleProductOptionToggle={handleProductOptionToggle}
            />
          )
        }
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='filterPanelModal'>
      <ModalOverlay fadeIn fadeOut />
      <ModalContent
        wrapperClassName='wrapper'
        contentClassName='content'
        ariaLabelledBy='filter-panel-label'
      >
        <div className='header'>
          <h3 id='filter-panel-label'>{t('filters.title')}</h3>
          <ModalCloseButton className='closeButton' />
        </div>
        <div className='panelBody'>
          {priceBounds && priceBounds.min < priceBounds.max && (
            <CollapsibleFilterSection
              title={t('filters.price')}
              titleId='filter-price-label'
              {...sectionProps(FilterType.Price)}
            >
              <PriceRangeSlider
                labelledById='filter-price-label'
                bounds={priceBounds}
                value={selectedPriceRange ?? priceBounds}
                onChange={({ min, max }) =>
                  handlePriceRangeChange(min, max, priceBounds)
                }
              />
            </CollapsibleFilterSection>
          )}
          {showAvailabilityFilter && (
            <InlineFilterSection>
              <ToggleSwitch
                id='inStockOnlyToggle'
                checked={inStockOnly}
                onChange={handleInStockOnlyToggle}
                label={t('filters.in-stock-only')}
                labelPosition='left'
                className='filterToggle'
              />
            </InlineFilterSection>
          )}
          {filterGroups.map((group) => {
            const section = buildFilterSection(group)
            if (section.inline) {
              return (
                <InlineFilterSection key={section.key}>
                  {section.content}
                </InlineFilterSection>
              )
            }
            return (
              <CollapsibleFilterSection
                key={section.key}
                title={section.title}
                {...sectionProps(section.key)}
              >
                {section.content}
              </CollapsibleFilterSection>
            )
          })}
        </div>
        <div className='footer'>
          <BrandedButton
            variant={ButtonType.Secondary}
            onClick={clearAll}
            className='clearAllFooterButton'
          >
            {t('filters.clear-all')}
          </BrandedButton>
          <BrandedButton
            variant={ButtonType.Primary}
            onClick={handleApply}
            className='applyFiltersButton'
          >
            {activeFilterCount > 0
              ? t('filters.apply-with-count', { count: activeFilterCount })
              : t('filters.apply')}
          </BrandedButton>
        </div>
      </ModalContent>
    </Modal>
  )
}

const CollapsibleFilterSection = ({
  title,
  titleId,
  children,
  expanded,
  onToggle,
  onEntered,
  onExit
}: {
  title: string
  titleId?: string
  children: React.ReactNode
  expanded: boolean
  onToggle: () => void
  onEntered?: () => void
  onExit?: () => void
}) => {
  return (
    <div className='filterSection'>
      <button
        className={classnames('filterSectionHeader', { expanded })}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <h4 id={titleId}>{title}</h4>
        <ChevronRightIcon accessibility='decorative' />
      </button>
      <AnimatedSection
        expanded={expanded}
        onEntered={onEntered}
        onExit={onExit}
      >
        <div className='filterOptions'>{children}</div>
      </AnimatedSection>
    </div>
  )
}

const InlineFilterSection = ({ children }: { children: React.ReactNode }) => {
  return <div className='filterSection inline'>{children}</div>
}
